import CorrelChart from './CorrelChart';
import './CorrelationCard.css';

interface CorrelationCardProps {
  baseQuestionIndex: number;
  baseQuestionText: string;
  baseAnswers: string[];
  correlatedQuestionIndex: number;
  correlatedQuestionText: string;
  correlatedAnswers: string[];
  counts: Record<number, Record<number, number>>;
  totalRespondents: number;
  explanationText: string;
}

function CorrelationCard({
  baseQuestionIndex,
  baseQuestionText,
  baseAnswers,
  correlatedQuestionIndex,
  correlatedQuestionText,
  correlatedAnswers,
  counts,
  totalRespondents,
  explanationText,
}: CorrelationCardProps) {
  // Format current date
  const formatDate = (): string => {
    const today = new Date();
    return `${today.getMonth() + 1}/${today.getDate()}/${today.getFullYear()}`;
  };

  // Generate grayscale color for legend
  const getLegendColor = (index: number): string => {
    const numChoices = correlatedAnswers.length;
    const lightness = 80 - (index / numChoices) * 60;
    const hue = 360 - (index / numChoices) * 360;
    return `hsl(${hue}, 0%, ${lightness}%)`;
  };

  return (
    <div className="correlation-card">
      <div className="correlation-card__header">
        <div className="correlation-card__code">
          {baseQuestionIndex + 1}:{correlatedQuestionIndex + 1}
        </div>
        <div className="correlation-card__total">
          {totalRespondents} total respondents
        </div>
        <div className="correlation-card__date">{formatDate()}</div>
      </div>

      <div className="correlation-card__panels">
        {/* Top Panel - Base Question with Charts */}
        <div className="correlation-card__panel-top">
          <h1 className="correlation-card__question-title">
            {baseQuestionIndex + 1}. {baseQuestionText}
          </h1>
          <div className="correlation-card__charts">
            {baseAnswers.map((answer, baseAnswerIndex) => {
              const chartCounts = counts[baseAnswerIndex] || {};
              return (
                <CorrelChart
                  key={baseAnswerIndex}
                  answerLabel={answer}
                  counts={chartCounts}
                  answerChoices={correlatedAnswers}
                />
              );
            })}
          </div>
        </div>

        {/* Bottom Panel - Correlated Question Legend */}
        <div className="correlation-card__panel-bottom">
          <h1 className="correlation-card__question-title">
            {correlatedQuestionIndex + 1}. {correlatedQuestionText}
          </h1>
          <div className="correlation-card__legend">
            {correlatedAnswers.map((answer, index) => (
              <div key={index} className="correlation-card__legend-item">
                <div
                  className="correlation-card__legend-box"
                  style={{ backgroundColor: getLegendColor(index) }}
                />
                <div className="correlation-card__legend-text">{answer}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="correlation-card__footer">
        <div className="correlation-card__explanation">{explanationText}</div>
      </div>
    </div>
  );
}

export default CorrelationCard;
